/**
 * TypeScript types matching API-CONTRACT.md v1.3
 */

// ============================================================
// Common Types
// ============================================================

export type UUID = string;

export interface ApiError {
  error: string;
  message?: string;
  details?: ValidationErrorDetail[];
  inspection_id?: UUID;
}

export interface ValidationErrorDetail {
  field: string;
  message: string;
}

// ============================================================
// 1.1 POST /inspections - Create Inspection
// ============================================================

export interface CreateInspectionRequest {
  plate: string;
  metadata: InspectionMetadata;
  photos: Record<string, File>; // keys: front, rear, side_right, side_left, dashboard, engine, trunk, chassis
  /** Vídeo motor→escape (opcional, contrato v1.3 §1.1) — habilita análise de fumaça/gotejamento */
  video?: File;
}

export interface InspectionMetadata {
  angles: Angle[];
  device_id: string;
  app_version: string;
  // Optional additional metadata
  timestamp?: string; // ISO 8601
  location?: {
    lat: number;
    lng: number;
    accuracy?: number;
  };
  user_agent?: string;
}

export type Angle =
  | 'front'
  | 'rear'
  | 'side_right'
  | 'side_left'
  | 'dashboard'
  | 'engine'
  | 'trunk'
  | 'chassis';

export const REQUIRED_ANGLES: Angle[] = [
  'front',
  'rear',
  'side_right',
  'side_left',
  'dashboard',
  'engine',
  'trunk',
  'chassis',
];

export interface CreateInspectionSuccessResponse {
  inspection_id: UUID;
  status: 'processing';
  received_at: string; // ISO 8601
}

export interface CreateInspectionErrorResponse extends ApiError {
  error: 'validation_error' | 'duplicate' | 'payload_too_large' | 'precheck_failed' | 'server_error';
  details?: ValidationErrorDetail[];
  reasons?: string[]; // for precheck_failed
  inspection_id?: UUID; // for duplicate
}

export type CreateInspectionResponse =
  | { success: true; data: CreateInspectionSuccessResponse }
  | { success: false; error: CreateInspectionErrorResponse; status: 400 | 409 | 413 | 422 | 500 };

// ============================================================
// 1.2 GET /inspections/status - Batch Status
// ============================================================

export type InspectionStatus = 'processing' | 'done' | 'failed';

export interface InspectionStatusItem {
  inspection_id: UUID;
  status: InspectionStatus;
  updated_at: string; // ISO 8601
  reason?: string; // only when failed
}

export type BatchStatusResponse = InspectionStatusItem[];

export interface BatchStatusErrorResponse extends ApiError {
  error: 'validation_error' | 'unauthorized' | 'not_found';
}

// ============================================================
// 1.3 GET /inspections/{id}/report - Full Report
// ============================================================

export type Grade = 'A' | 'B' | 'C';

export interface OptionalItem {
  name: string;
  detected: boolean;
  confidence: number; // 0-1
}

export interface Damage {
  id: string;
  label: string;
  severity: 'leve' | 'moderado' | 'grave';
  bbox: BoundingBox;
  original_photo_url: string;
  annotated_photo_url: string;
  /** Ângulo da foto onde a avaria foi detectada (v1.3) */
  angle?: Angle;
  /** Confiança do detector, 0-1 (v1.3) */
  confidence?: number;
}

export interface BoundingBox {
  x: number; // center x (0-1)
  y: number; // center y (0-1)
  width: number; // proportion (0-1)
  height: number; // proportion (0-1)
}

export interface InspectionReport {
  inspection_id: UUID;
  plate: string;
  grade: Grade;
  score: number; // 0-100
  optional_items: OptionalItem[];
  damages: Damage[];
  completed_at: string; // ISO 8601
  report_version: number;
}

export interface ReportErrorResponse extends ApiError {
  error: 'not_found' | 'expired' | 'unauthorized';
}

// ============================================================
// 1.4 POST /inspections/{id}/reprocess - Reprocess
// ============================================================

export interface ReprocessResponse {
  status: 'processing';
}

export interface ReprocessErrorResponse extends ApiError {
  error: 'not_found' | 'duplicate' | 'unauthorized';
}

// ============================================================
// Client App Specific Types
// ============================================================

export interface ClientInspectionState {
  // Local state before upload
  plate: string;
  photos: Partial<Record<Angle, { file: File; preview: string; timestamp: number }>>;
  /** Vídeo motor→escape capturado (opcional) */
  video?: { file: File; preview: string; timestamp: number };
  currentAngleIndex: number;
  isUploading: boolean;
  uploadProgress: number;
  
  // Server state after upload
  inspectionId?: UUID;
  serverStatus?: InspectionStatus;
  serverUpdatedAt?: string;
  failureReason?: string;
  report?: InspectionReport;
  
  // Error handling
  error?: string;
  errorCode?: string;
}

export interface ClientInspectionHistoryItem {
  inspectionId: UUID;
  plate: string;
  createdAt: string;
  status: InspectionStatus;
  grade?: Grade;
  score?: number;
  thumbnailUrl?: string;
}

// ============================================================
// Admin/Consultant App Types
// ============================================================

export interface AdminInspectionListItem {
  inspection_id: UUID;
  plate: string;
  store_id: string;
  store_name: string;
  status: InspectionStatus;
  grade?: Grade;
  score?: number;
  created_at: string;
  updated_at: string;
  client_name?: string;
  client_phone?: string;
  reason?: string; // only when failed
}

export interface AdminInspectionFilters {
  status?: InspectionStatus;
  grade?: Grade;
  store_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string; // plate or client name
  page: number;
  pageSize: number;
}

export interface AdminInspectionsResponse {
  items: AdminInspectionListItem[];
  total: number;
  page: number;
  pageSize: number;
}